/**
 * Client-side Error Logger Utility
 * Provides detailed error logging for debugging
 */

interface ErrorLogContext {
  endpoint?: string;
  method?: string;
  requestData?: any;
  responseData?: any;
  userId?: string | number;
  timestamp?: string;
  [key: string]: any;
}

class ErrorLogger {
  /**
   * Log error with detailed context
   */
  static logError(error: any, context: ErrorLogContext = {}) {
    const timestamp = new Date().toISOString();
    const errorDetails = {
      timestamp,
      error: {
        message: error?.message || 'Unknown error',
        name: error?.name || 'Error',
        code: error?.code,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data,
        stack: error?.stack,
      },
      context: {
        ...context,
        timestamp,
      },
    };

    // Console log with detailed information
    console.error('\n' + '='.repeat(80));
    console.error('🚨 CLIENT ERROR DETECTED');
    console.error('='.repeat(80));
    console.error(`📅 Timestamp: ${timestamp}`);
    if (context.endpoint) {
      console.error(`🌐 Endpoint: ${context.method || 'GET'} ${context.endpoint}`);
    }
    console.error(`🔴 Error Name: ${errorDetails.error.name}`);
    console.error(`💬 Error Message: ${errorDetails.error.message}`);
    
    if (errorDetails.error.status) {
      console.error(`📊 HTTP Status: ${errorDetails.error.status} ${errorDetails.error.statusText || ''}`);
    }
    
    if (errorDetails.error.code) {
      console.error(`🔢 Error Code: ${errorDetails.error.code}`);
    }

    if (context.requestData && Object.keys(context.requestData).length > 0) {
      console.error(`📤 Request Data:`, this.sanitizeData(context.requestData));
    }

    if (errorDetails.error.data) {
      console.error(`📥 Response Data:`, errorDetails.error.data);
    }

    if (context.userId) {
      console.error(`👤 User ID: ${context.userId}`);
    }

    if (errorDetails.error.stack) {
      console.error(`\n📚 Stack Trace:`);
      console.error(errorDetails.error.stack);
    }

    // Log additional context
    if (Object.keys(context).length > 0) {
      console.error(`\n📋 Additional Context:`, this.sanitizeData(context));
    }

    console.error('='.repeat(80) + '\n');

    // Optionally send to error tracking service (Sentry, etc.)
    // this.sendToErrorTracking(errorDetails);
  }

  /**
   * Log API error with request/response details
   */
  static logApiError(error: any, config?: any) {
    const context: ErrorLogContext = {
      endpoint: config?.url || error?.config?.url,
      method: config?.method?.toUpperCase() || error?.config?.method?.toUpperCase() || 'GET',
      requestData: config?.data || error?.config?.data,
      responseData: error?.response?.data,
      userId: this.getUserId(),
    };

    this.logError(error, context);
  }

  /**
   * Log general application error
   */
  static logAppError(error: any, component?: string, additionalContext?: any) {
    const context: ErrorLogContext = {
      component: component || 'Unknown',
      ...additionalContext,
      userId: this.getUserId(),
    };

    this.logError(error, context);
  }

  /**
   * Sanitize sensitive data before logging
   */
  private static sanitizeData(data: any): any {
    if (!data || typeof data !== 'object') {
      return data;
    }

    const sensitiveKeys = ['password', 'token', 'authorization', 'authToken', 'accessToken', 'refreshToken'];
    
    try {
      return JSON.parse(JSON.stringify(data, (key, value) => {
        if (sensitiveKeys.some(sk => key.toLowerCase().includes(sk.toLowerCase()))) {
          return '[REDACTED]';
        }
        return value;
      }));
    } catch (e) {
      return '[Unable to stringify]';
    }
  }

  /**
   * Get current user ID from session
   */
  private static getUserId(): string | number | undefined {
    try {
      const userStr = sessionStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.id || user._id;
      }
    } catch (e) {
      // Ignore parsing errors
    }
    return undefined;
  }

  /**
   * Log warning
   */
  static logWarning(message: string, context?: any) {
    console.warn('\n' + '⚠️ '.repeat(20));
    console.warn(`⚠️  WARNING: ${message}`);
    if (context) {
      console.warn('Context:', this.sanitizeData(context));
    }
    console.warn('⚠️ '.repeat(20) + '\n');
  }

  /**
   * Log info
   */
  static logInfo(message: string, data?: any) {
    console.log(`ℹ️  INFO: ${message}`);
    if (data) {
      console.log('Data:', this.sanitizeData(data));
    }
  }
}

export default ErrorLogger;







